import React, { useState, useEffect } from 'react'
import { AppIcons } from '../icons'
import { useAuth } from '../../hooks/useAuth'

interface UserProfileData {
  full_name: string
  email: string
  preferred_export_platform: string
  avatar_url: string | null
}

interface UserProfileProps {
  isOpen: boolean
  onClose: () => void
}

export function UserProfile({ isOpen, onClose }: UserProfileProps) {
  const { user, signOut, updateProfile, loading } = useAuth()
  const [profileData, setProfileData] = useState<UserProfileData>({
    full_name: '',
    email: '',
    preferred_export_platform: 'shutterstock',
    avatar_url: null,
  })
  const [isEditing, setIsEditing] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.user_metadata?.full_name || '',
        email: user.email || '',
        preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
        avatar_url: user.user_metadata?.avatar_url || null,
      })
    }
  }, [user])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveLoading(true)
    setMessage(null)

    try {
      const { error } = await updateProfile({
        full_name: profileData.full_name,
      })

      if (error) {
        setMessage({ type: 'error', text: error.message })
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully!' })
        setIsEditing(false)
        setTimeout(() => setMessage(null), 3000)
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update profile' })
    } finally {
      setSaveLoading(false)
    }
  }

  const handleSignOut = async () => {
    console.log('Sign out button clicked')
    try {
      const { error } = await signOut()
      console.log('Sign out result:', { error })
      if (!error) {
        console.log('Sign out successful, closing modal')
        onClose()
      } else {
        console.error('Sign out error:', error)
        setMessage({ type: 'error', text: error.message })
      }
    } catch (err) {
      console.error('Sign out exception:', err)
      setMessage({ type: 'error', text: 'Failed to sign out' })
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 lg:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] lg:max-h-none overflow-y-auto">
        <div className="flex items-center justify-between p-4 lg:p-6 border-b">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900">User Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
            style={{ minHeight: '44px', minWidth: '44px' }}
          >
            <AppIcons.X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 lg:p-6">
          {message && (
            <div className={`mb-4 p-3 rounded-md flex items-center ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {message.type === 'success' ? (
                <AppIcons.CheckCircle className="w-4 h-4 text-green-500 mr-2" />
              ) : (
                <AppIcons.AlertTriangle className="w-4 h-4 text-red-500 mr-2" />
              )}
              <span className={`text-sm ${
                message.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                {message.text}
              </span>
            </div>
          )}

          <form onSubmit={handleSave}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed text-base"
                style={{ minHeight: '44px' }}
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                disabled={!isEditing}
                className={`w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg text-base ${
                  isEditing ? 'focus:outline-none focus:ring-2 focus:ring-blue-500' : 'bg-gray-50'
                }`}
                style={{ minHeight: '44px' }}
                placeholder="Enter your full name"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Export Platform
              </label>
              <select
                value={profileData.preferred_export_platform}
                onChange={(e) => setProfileData({ ...profileData, preferred_export_platform: e.target.value })}
                disabled={!isEditing}
                className={`w-full px-3 py-3 lg:py-2 border border-gray-300 rounded-lg text-base ${
                  isEditing ? 'focus:outline-none focus:ring-2 focus:ring-blue-500' : 'bg-gray-50'
                }`}
                style={{ minHeight: '44px' }}
              >
                <option value="shutterstock">Shutterstock</option>
                <option value="adobe">Adobe Stock</option>
                <option value="istock">iStock</option>
                <option value="getty">Getty Images</option>
              </select>
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              {!isEditing ? (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="flex-1 bg-blue-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-base flex items-center justify-center"
                  style={{ minHeight: '44px' }}
                >
                  <AppIcons.Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false)
                      setMessage(null)
                      // Reset to original values
                      if (user) {
                        setProfileData({
                          full_name: user.user_metadata?.full_name || '',
                          email: user.email || '',
                          preferred_export_platform: user.user_metadata?.preferred_export_platform || 'shutterstock',
                          avatar_url: user.user_metadata?.avatar_url || null,
                        })
                      }
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 lg:py-2 px-4 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 font-medium text-base"
                    style={{ minHeight: '44px' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveLoading}
                    className="flex-1 bg-green-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 font-medium text-base"
                    style={{ minHeight: '44px' }}
                  >
                    {saveLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              )}
            </div>
          </form>

          <div className="mt-6 pt-6 border-t">
            <button
              onClick={handleSignOut}
              disabled={loading}
              className="w-full bg-red-600 text-white py-3 lg:py-2 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 font-medium text-base flex items-center justify-center"
              style={{ minHeight: '44px' }}
            >
              <AppIcons.LogOut className="w-4 h-4 mr-2" />
              {loading ? 'Signing out...' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}